import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Divider } from '@mui/material';
import { AppDispatch, RootState } from 'redux/store';
import Tab from 'components/Common/Tab';
import routes from 'constants/routes';
import Button from 'components/Common/CustomButton';
import { setSendAndRequestTab } from 'redux/slices/common/appSlice';
import SendMoneyContainer from 'components/SendAndRequest/SendMoneyContainer';
import SendRequestContainer from 'components/SendAndRequest/SendRequestContainer';
import { PENDING_REQUEST, SEND_MONEY, SEND_REQUEST, SendRequestTabs } from 'types/sendAndRequest';
import { fetchRequestMoneyActivityCounts } from 'redux/slices/sendAndRequest/requestMoneyActivitySlice';

const StyledContainer = styled.div({
    backgroundColor: '#F2F2F6',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
});

const StyledActionContainer = styled.div({
    marginBottom: 16,
    padding: 22,
});

const StyledInnerContainer = styled.div({
    backgroundColor: 'white',
    padding: 1,
    marginTop: 10,
});
const SendAndRequest: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const currentTab = useSelector((state: RootState) => state.app.sendAndRequest?.currentTab);
    const pending = useSelector((state: RootState) => state.sendAndRequest?.requestActivityList?.pending);

    useEffect(() => {
        dispatch(fetchRequestMoneyActivityCounts());
    }, []);

    const ButtonNavigationHandler = useCallback(() => {
        const targetRoute = currentTab === SEND_MONEY ? routes.sendAndRequest.sendMoney : routes.sendAndRequest.requestMoney;
        navigate(targetRoute);
    }, [currentTab, navigate]);

    // Switch Between Different Containers Based On Tab Value.
    const content = useMemo(() => {
        switch (currentTab) {
            case SEND_MONEY:
                return <SendMoneyContainer />;
            case SEND_REQUEST:
                return <SendRequestContainer />;
            case PENDING_REQUEST:
                return <SendRequestContainer intialFilter="all" requestStatus={['PENDING']} />;
            default:
                return null;
        }
    }, [currentTab]);

    const handleSetTab = useCallback(
        (tab: SendRequestTabs) => {
            dispatch(setSendAndRequestTab(tab));
        },
        [dispatch]
    );

    return (
        <StyledContainer style={{ borderRadius: 12 }}>
            <StyledActionContainer>
                <StyledInnerContainer>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: 22,
                            flexDirection: 'row',
                            justifyContent: 'space-around',
                        }}
                    >
                        <Tab
                            tabs={[
                                { value: SEND_MONEY, label: 'Send Money' },
                                {
                                    value: SEND_REQUEST,
                                    label: 'Request Money',
                                },
                                {
                                    value: PENDING_REQUEST,
                                    label: 'Pending Requests',
                                    badgeContent: pending,
                                    sx: {
                                        '& .MuiTab-iconWrapper': {
                                            px: '5px',
                                        },
                                        '& .MuiBadge-anchorOriginTopRight': {
                                            backgroundColor: '#B91C1C',
                                        },
                                    },
                                },
                            ]}
                            tabValue={currentTab}
                            setTabValue={handleSetTab}
                        />
                    </div>
                    <Divider />
                    {currentTab === SEND_REQUEST && (
                        <Button
                            onClick={() => navigate(routes.qr.main, { state: { showButton: true } })}
                            sx={{ m: 2, width: { xs: '50%', md: '25%' }, fontSize: { xs: 22, xl: 28 } }}
                            title={'View QR Code'}
                        />
                    )}
                    <Button
                        onClick={ButtonNavigationHandler}
                        sx={{
                            marginTop: '40px',
                            marginBottom: '35px',
                            m: 2,
                            width: { xs: '50%', md: '25%' },
                            fontSize: { xs: 22, xl: 28 },
                        }}
                        title={currentTab === SEND_MONEY ? 'Send Money' : 'Send Request'}
                    />
                    <div>{content}</div>
                </StyledInnerContainer>
            </StyledActionContainer>
        </StyledContainer>
    );
};

export default SendAndRequest;
